/* eslint-disable @typescript-eslint/no-explicit-any */
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  isDevMode,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { firstValueFrom, Subscription, take } from 'rxjs';
import { assets } from '../../../core/constants/assets.const';
import {
  buildOptionSpeakText,
  getOptionSubmissionValue,
  hasAiChatOptions,
  normalizeAiChatOptions,
} from '../../../core/helpers/ai-chat-options.helper';
import {
  getSpeakableAiMessageText,
  parseAiMessageContent,
} from '../../../core/helpers/ai-message-content.helper';
import {
  applyFemaleSpeechVoiceToUtterance,
  applySpeechPronunciations,
  deferSpeechStart,
  isSpeechSynthesisSupported,
  needsSpeechKeepAlive,
  resolveSpeechLang,
  speechVoiceQuality,
  startSpeechKeepAlive,
  waitForSpeechSynthesisVoices,
} from '../../../core/helpers/speech-synthesis.helper';
import {
  AiChatOption,
  AiConvoIdleMessage,
  aiModelStart,
  AIResponse,
  MessageType,
} from '../../../models/ai.model';
import {
  joinRoomResponse,
  receive,
  resolveChatCreatedAt,
  sendMessage,
} from '../../../models/chat.model';
import { AiChatIdleTimerService } from '../../../services/ai-chat-idle-timer.service';
import { aiService } from '../../../services/ai.service';
import { chatService } from '../../../services/chat.service';
import { chatSocketService } from '../../../services/chatSocket.service';
import { EncryptionService } from '../../../services/encryption.service';
import { I18nService } from '../../../services/i18n.service';
import { SpeechService } from '../../../services/speech.service';
import { VoiceAssistantService } from '../../../services/voice-assistant.service';
import { ToasterService } from '../../../services/toaster.service';
import { ImageUploadService } from '../../../services/uploadImage.service';
import { LinkifiedTextComponent } from '../linkified-text/linkified-text.component';

import { MalayalamTtsSpeakOptions } from '../../../models/malayalam-tts.model';
import { LoggerService } from '../../../services/logger.service';
import { MalayalamPiperTtsService } from '../../../services/malayalam-piper-tts.service';
import { MalayalamTtsService } from '../../../services/malayalam-tts.service';
import { appNoSpaceAtFirst } from '../../../core/directives/no-space.directive';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';

const AIPROMPT_FALLBACK_EN =
  'Welcome to LogiBids! Which service would you like to use today — Transport, Rental, or Delivery?';
const AI_START_PAYLOAD: aiModelStart = {
  navigation: 'HOME',
  step: 'MODULE',
};

type ChatMode = 'AI' | 'HYBRID';
type ServerChatType = 'HYBRID' | 'AI_BOOKING_OR_SUPPORT' | string;
type ChatMessageRole = 'ai' | 'user' | 'agent' | 'system' | 'info';

const AI_ASSISTANT_SENDER_ID = 'ai-assistant';

interface AiChatMessage {
  id?: string;
  message: string;
  sender: boolean;
  role?: ChatMessageRole;
  displayedMessage?: string;
  speakableMessage?: string;
  created_at?: string;
  sender_id?: string;
  sender_name?: string;
  sender_photo?: string | null;
  media?: string[];
  mediaRevealed?: boolean;
  helpGuideSlug?: string;
  type?: string;
  options?: AiChatOption[];
  selectedOption?: string;
  optionsLocked?: boolean;
  /** False while waiting for prior TTS/presentation to finish. */
  isRevealed?: boolean;
}

interface PresentationQueueItem {
  message: AiChatMessage;
  speak: boolean;
  onComplete?: () => void;
  /** When true, revealing this row ends the post-send input lock. */
  releaseAwaitingOnReveal?: boolean;
}

interface InitialBotPayload {
  message: string;
  options?: AiChatOption[];
}

@Component({
  selector: 'logibids-ai-cab-booking',
  imports: [
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    TranslatePipe,
    ReactiveFormsModule,
    InfiniteScrollModule,
    appNoSpaceAtFirst,
    LinkifiedTextComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './ai-cab-booking.component.html',
  styleUrl: './ai-cab-booking.component.scss',
  providers: [AiChatIdleTimerService],
})
export class AiCabBookingComponent implements OnDestroy, OnInit, AfterViewInit {
  private readonly _aiService = inject(aiService);
  private readonly destroy = inject(DestroyRef);
  private readonly speech = inject(SpeechService);
  private readonly voiceAssistant = inject(VoiceAssistantService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly toaster = inject(ToasterService);
  private readonly translate = inject(I18nService);
  private readonly router = inject(Router);
  private readonly encyrption = inject(EncryptionService);
  private readonly _chatService = inject(chatService);
  private readonly _chatSocketService = inject(chatSocketService);
  private readonly _uploadImageService = inject(ImageUploadService);
  private readonly malayalamTts = inject(MalayalamTtsService);
  private readonly malayalamPiperTts = inject(MalayalamPiperTtsService);
  private readonly logger = inject(LoggerService);
  private readonly dialogService = inject(DialogService);
  private readonly idleTimer = inject(AiChatIdleTimerService);

  private cameraDialogRef: DynamicDialogRef | null = null;

  readonly assetsConst = assets;

  messageControl = new FormControl<string>('', { nonNullable: true });

  loader = signal<boolean>(true);
  isListening = signal<boolean>(false);
  /** Hands-free conversation on: mic reopens after each reply. */
  readonly handsFree = this.voiceAssistant.handsFree;
  isSpeaking = signal<boolean>(false);
  isTypingBotResponse = signal<boolean>(false);
  isUploadingMedia = signal<boolean>(false);
  /** True from AI send until the API bot reply is shown on screen. */
  isAwaitingBotResponse = signal<boolean>(false);
  private apiResponseReceived = false;
  /** Blocks input while waiting for an API response or upload — not during TTS. */
  isInputDisabled = computed(
    () =>
      this.isUploadingMedia() ||
      this.isAwaitingBotResponse() ||
      this.isLoadingMessages() ||
      (this.loader() && !this.isTypingBotResponse())
  );
  isMicDisabled = computed(
    () =>
      this.isUploadingMedia() ||
      this.isAwaitingBotResponse() ||
      this.isLoadingMessages() ||
      (this.loader() && !this.isTypingBotResponse() && !this.isListening())
  );
  isBusy = computed(
    () =>
      this.isInputDisabled() ||
      this.isListening() ||
      this.isSpeaking() ||
      this.isTypingBotResponse()
  );

  volumeScale = signal<number>(1);
  private breatheInterval: any;
  transcript = '';
  private subs = new Subscription();
  readonly platformBrowser = isPlatformBrowser(this.platformId);

  @ViewChild('chatContainer', { read: ElementRef })
  chatContainer?: ElementRef<HTMLDivElement>;

  @ViewChild('aiVideo') aiVideo?: ElementRef<HTMLVideoElement>;

  langugae = signal<string>('');
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  /** Stops the Chrome online-voice keep-alive for the current utterance. */
  private stopSpeechKeepAlive: (() => void) | null = null;
  /** Last TTS voice logged, so the console only shows a line when it changes. */
  private loggedSpeechVoice = '';
  private synthesisVoices: SpeechSynthesisVoice[] = [];
  private bootstrapAborted = false;
  /** Ensures reopen TTS for last history message runs at most once per open. */
  private openTtsAttempted = false;
  private chatResizeObserver?: ResizeObserver;
  private scrollRafId: number | null = null;
  private scrollCursorTimeout: ReturnType<typeof setTimeout> | null = null;
  private chatScrollCursorCleanup: (() => void) | null = null;
  private activeChatId = '';
  private joinedChatId = '';
  private loadedMessageIds = new Set<string>();
  private loadedHistoryCount = 0;
  private historyLoadGeneration = 0;
  private continuingTicketResume = false;
  private socketMessageSub: Subscription | null = null;
  private suppressAutoScroll = false;
  private aiHandoffRefreshInFlight = false;
  private pendingAiHandoff = false;
  private handoffClosureDelivered = false;
  private speechSessionId = 0;
  private typingAnimationInterval: ReturnType<typeof setInterval> | null = null;
  private displaySyncInterval: ReturnType<typeof setInterval> | null = null;
  private activeSpeechMessage: AiChatMessage | null = null;
  private presentationQueue: PresentationQueueItem[] = [];
  private presentationProcessing = signal(false);
  /** Latest AI message that already started an idle period. */
  private idleArmedMessage: AiChatMessage | null = null;
  private readonly onSynthesisVoicesChanged = (): void => {
    this.refreshSynthesisVoices();
  };

  messagesData = signal<AiChatMessage[]>([]);
  chatMode = signal<ChatMode>('AI');
  activeAgentName = signal<string>('');
  activeAgentId = signal<string>('');
  readonly headerTitle = computed(() => {
    if (this.chatMode() === 'HYBRID' && this.activeAgentName()) {
      return this.activeAgentName();
    }
    return this.resolveTranslation('Chatbot', 'Chatbot');
  });
  historySize = signal<number>(10);
  historyFrom = signal<number>(1);
  historyTotal = signal<number>(0);
  historyFullyLoaded = signal(false);
  isLoadingMessages = signal(false);
  isMuted = signal<boolean>(false);
  step = 'MODULE';
  navigation = 'HOME';
  lat = signal<number>(0);
  lng = signal<number>(0);
  userProfile: any;
  intent: string = '';

  constructor() {
    if (this.platformBrowser) {
      this.langugae.set(localStorage.getItem('language') || 'en');
      this.userProfile = this.encyrption.decryptLocalStorage('profile');

      effect(() => {
        // Thinking, or speaking during hands-free (shows the mic will reopen).
        const handsFreeSpeaking =
          this.handsFree() && this.isTypingBotResponse();
        if (this.loader() || handsFreeSpeaking) {
          this.startBreathe();
        } else {
          this.stopBreathe();
        }
      });

      effect(() => {
        const msgs = this.messagesData();
        void msgs.length;
        if (!this.suppressAutoScroll && this.isChatNearBottom()) {
          this.scheduleScrollToBottom();
        }
      });

      effect(() => {
        if (this.isInputDisabled()) {
          this.messageControl.disable({ emitEvent: false });
        } else {
          this.messageControl.enable({ emitEvent: false });
        }
      });

      // Start the idle period once each new AI reply has been fully shown.
      effect(() => {
        if (this.chatMode() === 'HYBRID') {
          untracked(() => this.idleTimer.stop());
          return;
        }
        const settled =
          !this.isAwaitingBotResponse() &&
          !this.presentationProcessing() &&
          !this.isTypingBotResponse() &&
          !this.isLoadingMessages();
        const messages = this.messagesData();
        if (settled) {
          untracked(() => this.armIdleTimerForLatestReply(messages));
        }
      });
    }
  }

  ngAfterViewInit(): void {
    if (!this.platformBrowser) {
      return;
    }
    this.observeChatContentResize();
    this.bindChatScrollCursor();
    this.scheduleScrollToBottom();
  }

  ngOnInit() {
    if (!this.platformBrowser) return;

    const lang = localStorage.getItem('language') || 'en';
    if (this.malayalamTts.shouldUseServerTts(lang)) {
      void this.malayalamTts.preload();
    } else if (this.malayalamTts.isMalayalamLanguage(lang)) {
      void this.malayalamPiperTts.preload();
    }

    this.initSynthesisVoices();
    this.subs.add(
      this.speech.isListening$.subscribe(v => {
        this.isListening.set(v);
        // Clear loader both when listening starts and when it ends (avoids stuck loader
        // blocking the subsequent voice send for short words).
        this.loader.set(false);
      })
    );
    this.subs.add(
      this.speech.isSpeaking$.subscribe(v => this.isSpeaking.set(v))
    );
    this.subs.add(
      // Mirror the live STT text in the input; send() clears it when the API is called.
      this.speech.liveTranscript$.subscribe(text => {
        if (text && this.isListening()) {
          this.messageControl.setValue(text, { emitEvent: false });
        }
      })
    );
    this.subs.add(
      this.speech.volumeLevel$.subscribe(level => {
        // Only use volume level if we are not in loading state (breathe simulation)
        if (!this.loader()) {
          const scale = 1 + level * 0.35;
          this.volumeScale.set(scale);
        }
      })
    );
    this.subs.add(
      this.voiceAssistant.conversationUtterance$.subscribe(t => {
        this.transcript = t;
        if (this.transcript !== '') {
          // Voice transcripts must bypass listening/loader guards that can still be settling.
          void this.send(this.transcript, { fromVoice: true });
          this.cd.detectChanges();
        }
      })
    );
    this.idleTimer.configure({
      onFollowUp: message => this.showIdleFollowUp(message),
      onClosed: () => this.clearChatAfterIdleClose(),
    });
    this.voiceAssistant.notifyChatOpened();
    this.subs.add(
      this._chatSocketService
        .onReconnect()
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe(() => this.rejoinRoomAfterReconnect())
    );
    void this.bootstrapAssistant();
  }

  /** Re-join the stored room after a socket reconnect (server drops membership). */
  private rejoinRoomAfterReconnect(): void {
    if (!this.joinedChatId) {
      return;
    }
    this._chatSocketService.joinRoom({ chat_id: this.joinedChatId });
  }

  private async bootstrapAssistant(): Promise<void> {
    this.loader.set(true);
    this.openTtsAttempted = false;
    const lang = localStorage.getItem('language') || 'en';
    this.langugae.set(lang);

    try {
      await this.translate.ensureLoaded(lang);
      if (this.bootstrapAborted) {
        return;
      }

      this.synthesisVoices = await waitForSpeechSynthesisVoices();
      if (this.bootstrapAborted) {
        return;
      }

      if (this.malayalamTts.shouldUseServerTts(lang)) {
        void this.malayalamTts.preload();
      } else if (this.malayalamTts.isMalayalamLanguage(lang)) {
        void this.malayalamPiperTts.preload();
      }

      const welcomePayload = await this.getInitialPromptMessage();
      if (this.bootstrapAborted) {
        return;
      }

      if (this.malayalamTts.isMalayalamLanguage(lang) && welcomePayload) {
        await this.prefetchMalayalamWelcomeSpeech(welcomePayload);
      }

      if (welcomePayload) {
        this.initMessage(welcomePayload.message, welcomePayload.options);
      } else {
        // Resumed ticket / history load — speak the latest bot message (welcome is skipped).
        await this.speakLastConversationMessageOnOpen();
      }
    } finally {
      if (
        !this.bootstrapAborted &&
        !this.malayalamTts.isMalayalamLanguage(lang)
      ) {
        this.loader.set(false);
      }
    }
  }

  /** Warms TTS + welcome audio while keeping the orb loader visible for ml. */
  private async prefetchMalayalamWelcomeSpeech(
    payload: InitialBotPayload
  ): Promise<void> {
    const speakText = getSpeakableAiMessageText(payload.message);
    const tts = this.malayalamTts.shouldUseServerTts(this.langugae())
      ? this.malayalamTts
      : this.malayalamPiperTts;

    void tts.prepareSpeech(speakText);

    for (const option of normalizeAiChatOptions(payload.options)) {
      void tts.prepareSpeech(buildOptionSpeakText(option));
    }

    await tts.preload();
    await tts.prepareSpeech(speakText);
  }

  private async getInitialPromptMessage(): Promise<InitialBotPayload | null> {
    try {
      const res = await firstValueFrom(
        this._aiService.startConversationToAi(AI_START_PAYLOAD)
      );
      const preservePayloadState = this.isSupportStepResponse(res.data);
      this.applyAgentMeta(res.data);
      const resumedTicket = await this.handleSupportChatResponse(res.data, {
        preservePayloadStateOnSupport: true,
      });

      if (resumedTicket) {
        this.appendMessagesFromApiResponse(res.data, {
          speakChat: false,
          speakStatus: false,
          statusOnly: true,
        });
        return null;
      }

      const { chatMessage } = this.appendMessagesFromApiResponse(res.data, {
        deferChatAppend: true,
      });

      if (chatMessage) {
        if (!preservePayloadState) {
          this.updateAiPayloadState(res.data);
        }
        return {
          message: chatMessage.message,
          options: chatMessage.options,
        };
      }

      if (!preservePayloadState) {
        this.updateAiPayloadState(res.data);
      }
    } catch (error) {
      console.warn('AI start conversation failed', error);
    }

    return {
      message: this.resolveTranslation('AIPrompt', AIPROMPT_FALLBACK_EN),
    };
  }

  private resolveTranslation(key: string, fallback: string): string {
    return this.translate.hasTranslationKey(key)
      ? this.translate.translate(key)
      : fallback;
  }

  formatMessageTimestamp(createdAt?: string): string {
    if (!createdAt?.trim()) {
      return '';
    }

    const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(createdAt)
      ? createdAt.replace(' ', 'T')
      : createdAt;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const day = date.getDate();
    const month = date.toLocaleString('en', { month: 'long' });
    const time = date.toLocaleString('en', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    return `${day} ${month} at ${time}`;
  }

  private resolveMessageRole(
    chat: receive,
    isCurrentUser: boolean
  ): ChatMessageRole {
    if (isCurrentUser) {
      return 'user';
    }

    const semanticType = (chat.message_type ?? '').toUpperCase();
    if (semanticType === MessageType.SYSTEM) {
      return 'system';
    }
    if (semanticType === 'INFO') {
      return 'info';
    }

    const contentType = (chat.type ?? '').toUpperCase();
    if (contentType === MessageType.SYSTEM) {
      return 'system';
    }
    if (contentType === 'INFO') {
      return 'info';
    }

    if (this.chatMode() === 'HYBRID') {
      return this.isAiAssistantMessage(chat) ? 'ai' : 'agent';
    }

    return 'ai';
  }

  private isAiAssistantMessage(chat: receive): boolean {
    return chat.sender_id === AI_ASSISTANT_SENDER_ID;
  }

  private syncActiveAgentFromMessages(messages: AiChatMessage[]): void {
    for (let index = messages.length - 1; index >= 0; index--) {
      const message = messages[index];
      if (message.role === 'agent' && message.sender_name?.trim()) {
        this.activeAgentName.set(message.sender_name.trim());
        return;
      }
    }
  }

  private updateActiveAgentFromMessage(message: AiChatMessage): void {
    if (message.role === 'agent' && message.sender_name?.trim()) {
      this.activeAgentName.set(message.sender_name.trim());
    }
  }

  private createSystemMessage(
    text: string,
    role: 'system' | 'info' = 'system'
  ): AiChatMessage {
    return {
      message: text,
      displayedMessage: text,
      sender: false,
      role,
      created_at: new Date().toISOString(),
    };
  }

  shouldShowChatMessage(message: AiChatMessage): boolean {
    return message.isRevealed !== false;
  }

  /** Hides the bottom pending bubble once a socket/API status line is already visible. */
  shouldShowAwaitingBotBubble(): boolean {
    if (!this.isAwaitingBotResponse() || this.chatMode() === 'HYBRID') {
      return false;
    }

    const visibleMessages = this.messagesData().filter(item =>
      this.shouldShowChatMessage(item)
    );
    const lastMessage = visibleMessages[visibleMessages.length - 1];

    // Hide once a socket/API line is already on screen; keep only after the user's send.
    return lastMessage?.sender === true || lastMessage?.role === 'user';
  }

  shouldShowAiMessageLoader(message: AiChatMessage): boolean {
    if (message.role !== 'ai' && message.sender !== false) {
      return false;
    }

    if ((message.displayedMessage || '').trim()) {
      return false;
    }

    const hasPendingContent =
      !!(message.message || '').trim() ||
      hasAiChatOptions(message.options) ||
      !!message.media?.length;

    return (
      hasPendingContent &&
      (this.isTypingBotResponse() || this.presentationProcessing())
    );
  }

  private shouldDeferMessageReveal(): boolean {
    return (
      this.presentationProcessing() ||
      this.presentationQueue.length > 0 ||
      this.isTypingBotResponse()
    );
  }

  /**
   * Appends a chat row and optionally speaks it. Rows stay hidden until prior
   * presentation/TTS completes so avatar, text, and timestamp stay in sync.
   */
  private appendChatMessage(
    message: AiChatMessage,
    options: {
      speak?: boolean;
      revealImmediately?: boolean;
      onComplete?: () => void;
      releaseAwaitingOnReveal?: boolean;
    } = {}
  ): void {
    const speak = options.speak === true;
    const revealImmediately = options.revealImmediately === true;

    if (
      speak &&
      message.role === 'ai' &&
      (message.message || '').trim() &&
      !message.displayedMessage
    ) {
      message.displayedMessage = '';
    }

    const awaitsTypewriter =
      speak && message.role === 'ai' && !!(message.message || '').trim();
    const deferReveal =
      !revealImmediately &&
      (this.shouldDeferMessageReveal() || awaitsTypewriter);
    message.isRevealed = !deferReveal;

    this.messagesData.update(prev => [...prev, message]);

    if (deferReveal || speak) {
      this.presentationQueue.push({
        message,
        speak,
        onComplete: options.onComplete,
        releaseAwaitingOnReveal: options.releaseAwaitingOnReveal,
      });
      this.processPresentationQueue();
      return;
    }

    this.scheduleScrollToBottom();
  }

  private processPresentationQueue(): void {
    if (this.presentationProcessing()) {
      return;
    }

    void this.runPresentationQueue();
  }

  private async runPresentationQueue(): Promise<void> {
    this.presentationProcessing.set(true);

    try {
      while (this.presentationQueue.length > 0) {
        const item = this.presentationQueue.shift();
        if (!item) {
          break;
        }

        const { message, speak, onComplete, releaseAwaitingOnReveal } = item;
        message.isRevealed = true;
        this.onApiResponseMessageRevealed(message, releaseAwaitingOnReveal);
        this.cd.detectChanges();
        this.scheduleScrollToBottom();

        if (
          speak &&
          this.shouldSpeakMessageRole(message.role) &&
          this.hasSpeakableMessageContent(message)
        ) {
          this.voiceAssistant.notifyTtsStarted();
          await new Promise<void>(resolve => {
            void this.animateAndSpeakMessage(message, () => {
              onComplete?.();
              resolve();
            });
          });
        } else {
          this.revealMessageMedia(message);
          this.cd.detectChanges();
          this.scheduleScrollToBottom();
          onComplete?.();
        }
      }
    } finally {
      this.presentationProcessing.set(false);

      if (this.presentationQueue.length > 0) {
        this.processPresentationQueue();
      } else if (!this.isTypingBotResponse()) {
        this.tryCompleteAiSendTurn();
        this.voiceAssistant.notifyPresentationSettled();
      }
    }
  }

  private onApiResponseMessageRevealed(
    message: AiChatMessage,
    releaseAwaitingOnReveal?: boolean
  ): void {
    if (
      !releaseAwaitingOnReveal ||
      !this.isAwaitingBotResponse() ||
      !this.apiResponseReceived
    ) {
      return;
    }

    if (message.role === 'ai') {
      this.releaseInputAfterBotResponse();
    }
  }

  private tryCompleteAiSendTurn(): void {
    if (!this.isAwaitingBotResponse() || !this.apiResponseReceived) {
      return;
    }

    if (this.presentationProcessing() || this.presentationQueue.length > 0) {
      return;
    }

    this.releaseInputAfterBotResponse();
  }

  private releaseInputAfterBotResponse(): void {
    this.apiResponseReceived = false;
    this.isAwaitingBotResponse.set(false);
    this.loader.set(false);
  }

  private finalizeBotResponseIfIdle(): void {
    this.tryCompleteAiSendTurn();
  }

  private flushPresentationQueueOnInterrupt(): void {
    this.presentationQueue = [];
    this.presentationProcessing.set(false);
    this.messagesData.update(messages =>
      messages.map(message =>
        message.isRevealed === false
          ? { ...message, isRevealed: true }
          : message
      )
    );
  }

  private applyAgentMeta(
    source: Partial<
      Pick<
        AIResponse['data'],
        'agent_name' | 'agent_id' | 'chat_type' | 'chat_id'
      > &
        Pick<receive, 'agent_name' | 'agent_id' | 'chat_type' | 'chat_id'>
    >
  ): void {
    if (source.agent_name?.trim()) {
      this.activeAgentName.set(source.agent_name.trim());
    }

    if (source.agent_id?.trim()) {
      this.activeAgentId.set(source.agent_id.trim());
    }

    if (source.chat_type) {
      const chatId = source.chat_id
        ? String(source.chat_id)
        : this.activeChatId || undefined;
      this.applyChatModeFromType(source.chat_type, chatId);
    }
  }

  private resolveApiMessageType(data: AIResponse['data']): MessageType {
    const messageType = (data.message_type ?? MessageType.CHAT).toUpperCase();
    return messageType === MessageType.SYSTEM
      ? MessageType.SYSTEM
      : MessageType.CHAT;
  }

  private hasStatusMessage(text: string): boolean {
    return this.messagesData().some(
      message =>
        (message.role === 'system' || message.role === 'info') &&
        message.message === text
    );
  }

  /**
   * Appends API-driven status and chat messages from a response payload.
   * Returns the CHAT bot message when one was created (for deferred append/TTS).
   */
  private appendMessagesFromApiResponse(
    data: AIResponse['data'],
    options: {
      speakChat?: boolean;
      speakStatus?: boolean;
      deferChatAppend?: boolean;
      statusOnly?: boolean;
    } = {}
  ): { chatMessage: AiChatMessage | null } {
    const statusMessages: AiChatMessage[] = [];

    const preMessage = data.pre_decision_tree_message?.trim();
    if (preMessage && !this.hasStatusMessage(preMessage)) {
      statusMessages.push(this.createSystemMessage(preMessage, 'info'));
    }

    const mainMessage = data.message?.trim();
    const apiMedia = this.normalizeApiMedia(data.media);
    let chatMessage: AiChatMessage | null = null;

    if (mainMessage || apiMedia.length > 0) {
      if (
        mainMessage &&
        this.resolveApiMessageType(data) === MessageType.SYSTEM
      ) {
        if (!this.hasStatusMessage(mainMessage)) {
          statusMessages.push(this.createSystemMessage(mainMessage, 'system'));
        }
      } else {
        chatMessage = this.createBotMessage(
          mainMessage ?? '',
          data.options,
          apiMedia,
          data.help_guide_slug
        );
      }
    }

    const speakStatus = options.speakStatus !== false;
    const speakChat = options.speakChat !== false;

    if (statusMessages.length > 0) {
      for (const statusMessage of statusMessages) {
        this.appendChatMessage(statusMessage, { speak: speakStatus });
      }
    }

    if (chatMessage && !options.deferChatAppend && !options.statusOnly) {
      this.appendChatMessage(chatMessage, {
        speak: speakChat,
        releaseAwaitingOnReveal: true,
      });
    }

    return { chatMessage };
  }

  /**
   * When the AI API returns chat_id, join the socket room immediately.
   * HYBRID send/receive flow starts only when chat_type === 'HYBRID'.
   * Chat history loads only when continuing_ticket is true.
   */
  private async handleSupportChatResponse(
    data: AIResponse['data'],
    options: {
      preservePayloadStateOnSupport?: boolean;
    } = {}
  ): Promise<boolean> {
    if (!data.chat_id) {
      this.continuingTicketResume = false;
      return false;
    }

    this.applyAgentMeta(data);

    const preservePayloadState =
      options.preservePayloadStateOnSupport === true &&
      this.isSupportStepResponse(data);

    if (!preservePayloadState) {
      this.updateAiPayloadState(data);
    }

    const shouldResumeTicket = data.continuing_ticket === true;
    this.continuingTicketResume = shouldResumeTicket;

    await this.connectSupportChat(data.chat_id, {
      loadHistory: shouldResumeTicket,
      chatType: data.chat_type,
    });

    return shouldResumeTicket;
  }

  private canLoadChatHistory(): boolean {
    return this.chatMode() === 'HYBRID' || this.continuingTicketResume;
  }

  private isSupportStepResponse(data: AIResponse['data']): boolean {
    return data.step === 'SUPPORT' || data.next_step === 'SUPPORT';
  }

  private updateAiPayloadState(data: AIResponse['data']): void {
    this.navigation = data.next_navigation || this.navigation;

    if (!this.isSupportStepResponse(data)) {
      this.step = this.normalizeOutgoingAiStep(data.next_step || this.step);
    }
  }

  private getOutgoingAiStep(): string {
    return this.normalizeOutgoingAiStep(this.step);
  }

  private normalizeOutgoingAiStep(step: string): string {
    if (step === 'SUPPORT' || step === 'INITIAL') {
      return AI_START_PAYLOAD.step;
    }

    return step;
  }

  private async connectSupportChat(
    chatId: string,
    options: {
      loadHistory?: boolean;
      chatType?: ServerChatType;
      fromSocket?: boolean;
    } = {}
  ): Promise<void> {
    const previousChatId = this.activeChatId;

    this.ensureSocketRoom(chatId);

    this.activeChatId = chatId;

    this.applyChatModeFromType(
      options.chatType,
      chatId,
      options.fromSocket === true
    );

    if (chatId !== previousChatId) {
      this.resetHistoryState();
    }

    if (options.loadHistory) {
      if (chatId === previousChatId) {
        this.resetHistoryState();
      }
      await this.loadChatHistory(true);
    }
  }

  private ensureSocketRoom(chatId: string): void {
    if (this.socketMessageSub && this.joinedChatId === chatId) {
      return;
    }

    this.leaveJoinedSocketRoom();
    this.joinChatRoom(chatId);
  }

  private applyChatModeFromType(
    chatType?: ServerChatType,
    chatId?: string,
    fromSocket = false
  ): void {
    if (!chatType) {
      return;
    }

    if (chatType === 'HYBRID') {
      if (chatId) {
        this.chatMode.set('HYBRID');
      }
      return;
    }

    if (chatType === 'AI_BOOKING_OR_SUPPORT') {
      const wasHybrid = this.chatMode() === 'HYBRID';
      this.chatMode.set('AI');
      this.navigation = AI_START_PAYLOAD.navigation;
      this.step = AI_START_PAYLOAD.step;

      if (wasHybrid || fromSocket) {
        this.pendingAiHandoff = true;
      }

      if (wasHybrid) {
        this.closeCameraDialogIfOpen();
      }
    }
  }

  private clearMessagesForAiHandoff(): void {
    this.cancelScheduledScroll();
    this.interruptAssistantSpeech();
    this.presentationQueue = [];
    this.presentationProcessing.set(false);
    this.resetHistoryState();
    this.messagesData.set([]);
    this.handoffClosureDelivered = false;
    this.suppressAutoScroll = false;
    this.scheduleScrollToBottom();
  }

  private completePendingAiHandoff(): void {
    if (!this.pendingAiHandoff || this.aiHandoffRefreshInFlight) {
      return;
    }

    this.pendingAiHandoff = false;
    this.scheduleAssistantHandoffRefresh();
  }

  /** Starts the initial AI welcome flow without waiting for user input. */
  private scheduleAssistantHandoffRefresh(): void {
    if (this.aiHandoffRefreshInFlight) {
      return;
    }

    this.aiHandoffRefreshInFlight = true;
    void this.refreshAssistantFromInitial().finally(() => {
      this.aiHandoffRefreshInFlight = false;
      this.handoffClosureDelivered = false;
    });
  }

  private resetHistoryState(): void {
    this.historyLoadGeneration += 1;
    this.historyFrom.set(1);
    this.historyTotal.set(0);
    this.loadedHistoryCount = 0;
    this.loadedMessageIds = new Set<string>();
    this.historyFullyLoaded.set(false);
  }

  async loadChatHistory(initialLoad = false): Promise<void> {
    if (!this.activeChatId || this.isLoadingMessages()) {
      return;
    }

    if (!this.canLoadChatHistory()) {
      return;
    }

    if (!initialLoad && this.historyFullyLoaded()) {
      return;
    }

    if (
      !initialLoad &&
      this.historyTotal() > 0 &&
      this.loadedHistoryCount >= this.historyTotal()
    ) {
      this.historyFullyLoaded.set(true);
      return;
    }

    const loadGeneration = this.historyLoadGeneration;
    const chatIdForLoad = this.activeChatId;

    this.isLoadingMessages.set(true);

    const shouldPreserveScroll = !initialLoad;
    const beforeHeight = this.chatContainer?.nativeElement.scrollHeight;
    const beforeTop = this.chatContainer?.nativeElement.scrollTop;
    const query = `from=${this.historyFrom()}&size=${this.historySize()}`;
    const hasLiveMessagesOnly =
      !initialLoad &&
      this.loadedHistoryCount === 0 &&
      this.messagesData().length > 0;

    if (shouldPreserveScroll) {
      this.suppressAutoScroll = true;
      this.cancelScheduledScroll();
    }

    try {
      const res = await firstValueFrom(
        this._chatService
          .getChatsById(chatIdForLoad, query)
          .pipe(takeUntilDestroyed(this.destroy))
      );

      if (
        loadGeneration !== this.historyLoadGeneration ||
        chatIdForLoad !== this.activeChatId ||
        !this.canLoadChatHistory()
      ) {
        return;
      }

      const newMessages = res.list
        .slice()
        .reverse()
        .map(chat => this.mapHistoryMessage(chat))
        .filter(message => this.trackLoadedMessage(message));

      this.historyTotal.set(res.total);
      this.historyFrom.update(value => value + 1);
      this.loadedHistoryCount += newMessages.length;

      if (
        this.loadedHistoryCount >= res.total ||
        (!initialLoad && newMessages.length === 0)
      ) {
        this.historyFullyLoaded.set(true);
      }

      if (initialLoad) {
        if (this.continuingTicketResume) {
          // Keep the last bot line empty so open TTS can type it word-by-word
          // instead of flashing the full transcript first.
          for (let index = newMessages.length - 1; index >= 0; index--) {
            const candidate = newMessages[index];
            if (
              this.shouldSpeakMessageRole(candidate.role) &&
              !!(candidate.message || '').trim()
            ) {
              candidate.displayedMessage = '';
              if (candidate.media?.length) {
                candidate.mediaRevealed = false;
              }
              break;
            }
          }
        }
        this.messagesData.set(newMessages);
        this.syncActiveAgentFromMessages(newMessages);
        this.scheduleScrollToBottom();
        // Backup: if bootstrap already moved on, still TTS the latest bot line.
        if (this.continuingTicketResume && !this.openTtsAttempted) {
          void this.speakLastConversationMessageOnOpen();
        }
      } else if (hasLiveMessagesOnly) {
        // Page 1 overlaps messages already shown at the bottom — upgrade ids only.
        this.messagesData.update(prev =>
          this.upgradeExistingWithHistory(newMessages, prev)
        );
      } else if (newMessages.length > 0) {
        this.messagesData.update(prev =>
          this.prependUniqueHistoryMessages(newMessages, prev)
        );
      }
    } catch (error) {
      console.error('Failed to load chat history', error);
    } finally {
      if (
        loadGeneration === this.historyLoadGeneration &&
        chatIdForLoad === this.activeChatId
      ) {
        this.isLoadingMessages.set(false);
        this.loader.set(false);
      }
      if (shouldPreserveScroll) {
        this.restoreHistoryScrollPosition(beforeHeight, beforeTop);
      }
    }
  }

  onHistoryScrolledUp(): void {
    if (
      !this.activeChatId ||
      !this.canLoadChatHistory() ||
      this.isLoadingMessages() ||
      this.historyFullyLoaded()
    ) {
      return;
    }
    void this.loadChatHistory(false);
  }

  private resolveMessageCreatedAt(
    source?: Partial<receive> | AiChatMessage | Record<string, unknown> | null
  ): string | undefined {
    return resolveChatCreatedAt(source);
  }

  private normalizeIncomingChatPayload(raw: receive): receive {
    const payload = ((raw as { data?: receive }).data ?? raw) as receive;

    return {
      ...payload,
      created_at: this.resolveMessageCreatedAt(payload) ?? payload.created_at,
    };
  }

  private mapHistoryMessage(chat: receive): AiChatMessage {
    const normalizedChat = this.normalizeIncomingChatPayload(chat);
    const isCurrentUser = normalizedChat.sender_id === this.userProfile?.id;
    const message = normalizedChat.content ?? '';
    const role = this.resolveMessageRole(normalizedChat, isCurrentUser);
    const createdAt = this.resolveMessageCreatedAt(normalizedChat);

    const mappedMessage: AiChatMessage = {
      id: String(normalizedChat.id),
      message,
      displayedMessage:
        isCurrentUser || role === 'system' || role === 'info'
          ? message
          : message,
      sender: isCurrentUser,
      role,
      created_at: createdAt,
      sender_id: normalizedChat.sender_id,
      sender_name: normalizedChat.sender_name,
      sender_photo: normalizedChat.sender_photo,
      media: normalizedChat.media ?? undefined,
      helpGuideSlug: normalizedChat.help_guide_slug?.trim() || undefined,
      type: normalizedChat.type,
      options: normalizeAiChatOptions(normalizedChat.options),
      selectedOption: normalizedChat.selected_option?.name,
      optionsLocked: !!normalizedChat.selected_option,
    };

    if (role === 'system' || role === 'info') {
      return { ...mappedMessage, isRevealed: true };
    }

    if (isCurrentUser) {
      return { ...mappedMessage, isRevealed: true };
    }

    if (role === 'agent') {
      this.updateActiveAgentFromMessage(mappedMessage);
    }

    return this.normalizeBotMessage({
      ...mappedMessage,
      displayedMessage: mappedMessage.message,
      isRevealed: true,
    });
  }

  private normalizeApiMedia(media?: string[] | null): string[] {
    if (!media?.length) {
      return [];
    }

    const unique = new Set<string>();
    for (const url of media) {
      const trimmed = url?.trim();
      if (trimmed) {
        unique.add(trimmed);
      }
    }

    return [...unique];
  }

  private createBotMessage(
    message: string,
    options?: AiChatOption[],
    media?: string[],
    helpGuideSlug?: string
  ): AiChatMessage {
    const normalizedMedia = this.normalizeApiMedia(media);
    const normalizedHelpGuideSlug = helpGuideSlug?.trim() || undefined;

    return this.normalizeBotMessage({
      message,
      displayedMessage: '',
      sender: false,
      role: 'ai',
      created_at: new Date().toISOString(),
      options: normalizeAiChatOptions(options),
      media: normalizedMedia.length > 0 ? normalizedMedia : undefined,
      helpGuideSlug: normalizedHelpGuideSlug,
    });
  }

  /** Strips embedded resource URLs from bot copy and extracts them as media attachments. */
  private normalizeBotMessage(message: AiChatMessage): AiChatMessage {
    if (message.sender) {
      return message;
    }

    const normalizedOptions = normalizeAiChatOptions(message.options);
    const optionsField =
      normalizedOptions.length > 0 ? normalizedOptions : undefined;

    if (!message.message?.trim()) {
      return {
        ...message,
        options: optionsField,
        media: message.media?.length ? message.media : undefined,
      };
    }

    const parsed = parseAiMessageContent(message.message);
    const existingMedia = message.media ?? [];
    const mergedMedia =
      parsed.media.length > 0
        ? [
            ...existingMedia,
            ...parsed.media.filter(url => !existingMedia.includes(url)),
          ]
        : existingMedia;

    return {
      ...message,
      message: parsed.displayText,
      speakableMessage: parsed.speakableText,
      displayedMessage: message.displayedMessage
        ? parsed.displayText
        : message.displayedMessage,
      media: mergedMedia.length > 0 ? mergedMedia : undefined,
      options: optionsField,
    };
  }

  private trackLoadedMessage(message: AiChatMessage): boolean {
    if (message.id) {
      if (this.loadedMessageIds.has(message.id)) {
        return false;
      }
      this.loadedMessageIds.add(message.id);
      return true;
    }

    return !this.isDuplicateInList(this.messagesData(), message);
  }

  private prependUniqueHistoryMessages(
    incoming: AiChatMessage[],
    existing: AiChatMessage[]
  ): AiChatMessage[] {
    const uniqueIncoming = incoming.filter(
      message => !this.isDuplicateInList(existing, message)
    );
    return uniqueIncoming.length > 0
      ? [...uniqueIncoming, ...existing]
      : existing;
  }

  private upgradeExistingWithHistory(
    history: AiChatMessage[],
    existing: AiChatMessage[]
  ): AiChatMessage[] {
    return existing.map(message => {
      const match = history.find(
        item =>
          (message.id && item.id && message.id === item.id) ||
          this.areSameChatMessage(message, item)
      );

      if (!match) {
        return message;
      }

      return {
        ...message,
        id: match.id ?? message.id,
        created_at: match.created_at ?? message.created_at,
        sender_photo: match.sender_photo ?? message.sender_photo,
      };
    });
  }

  private isDuplicateInList(
    messages: AiChatMessage[],
    candidate: AiChatMessage
  ): boolean {
    return messages.some(message =>
      this.areSameChatMessage(message, candidate)
    );
  }

  private areSameChatMessage(
    first: AiChatMessage,
    second: AiChatMessage
  ): boolean {
    if (first.id && second.id) {
      return first.id === second.id;
    }

    if (first.sender !== second.sender) {
      return false;
    }

    const firstText = (first.message || first.displayedMessage || '').trim();
    const secondText = (second.message || second.displayedMessage || '').trim();
    if (firstText && secondText && firstText === secondText) {
      return true;
    }

    if (first.media?.length && second.media?.length) {
      return this.isSameMediaSet(first.media, second.media);
    }

    return false;
  }

  trackChatMessage(_index: number, message: AiChatMessage): string | number {
    if (message.id) {
      return message.id;
    }

    const text = (message.message || message.displayedMessage || '').trim();
    const mediaKey = message.media?.join('|') ?? '';
    return `${message.sender}-${text}-${mediaKey}-${message.created_at ?? _index}`;
  }

  private preserveScrollPosition(
    beforeHeight: number,
    beforeTop: number
  ): void {
    const el = this.chatContainer?.nativeElement;
    if (!el) {
      return;
    }

    const afterHeight = el.scrollHeight;
    const previousScrollBehavior = el.style.scrollBehavior;
    el.style.scrollBehavior = 'auto';
    el.scrollTop = beforeTop + afterHeight - beforeHeight;
    el.style.scrollBehavior = previousScrollBehavior;
  }

  private restoreHistoryScrollPosition(
    beforeHeight?: number,
    beforeTop?: number
  ): void {
    setTimeout(() => {
      if (beforeHeight !== undefined && beforeTop !== undefined) {
        this.preserveScrollPosition(beforeHeight, beforeTop);
      }
      requestAnimationFrame(() => {
        if (beforeHeight !== undefined && beforeTop !== undefined) {
          this.preserveScrollPosition(beforeHeight, beforeTop);
        }
        requestAnimationFrame(() => {
          if (beforeHeight !== undefined && beforeTop !== undefined) {
            this.preserveScrollPosition(beforeHeight, beforeTop);
          }
          this.suppressAutoScroll = false;
        });
      });
    }, 0);
  }

  private joinChatRoom(chatId: string): void {
    this.teardownSocketListener();
    this.joinedChatId = chatId;
    this._chatSocketService.joinRoom({ chat_id: chatId });

    this.subs.add(
      this._chatSocketService
        .getJoinRoomResponse()
        .pipe(takeUntilDestroyed(this.destroy), take(1))
        .subscribe((response: joinRoomResponse) => {
          if (response.status === 'success') {
            const resolvedChatId = response.chat_id || chatId;
            this.activeChatId = resolvedChatId;
            this.joinedChatId = resolvedChatId;
            this.applyChatModeFromType(response.chat_type, resolvedChatId);
          }
        })
    );

    this.socketMessageSub = this._chatSocketService
      .receiverMessage()
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe(message => {
        this.handleIncomingSocketMeta(message);
        if (!this.isSocketMetaOnlyMessage(message)) {
          this.appendSocketMessage(message);
        }
      });
    this.subs.add(this.socketMessageSub);
  }

  /** Mirrors API chat_id / chat_type handling for socket payloads. */
  private handleIncomingSocketMeta(message: receive): void {
    if (this.isTerminalChatSocketStatus(message.status)) {
      this.closeCameraDialogIfOpen();
      this.resetHistoryState();

      if (this.chatMode() === 'HYBRID' || this.joinedChatId) {
        this.pendingAiHandoff = true;
      }
    }

    // Apply mode changes synchronously before appendSocketMessage runs so
    // handoff sequencing (closure TTS → welcome) is set up in time.
    this.applyAgentMeta(message);
    this.applyChatModeFromType(
      message.chat_type,
      this.joinedChatId || undefined,
      true
    );

    const incomingChatId = message.chat_id ? String(message.chat_id) : '';

    // Switch rooms only when the server points us to a genuinely different
    // chat_id. Re-joining for messages on the current room would tear down
    // the active listener and drop incoming/echoed messages.
    if (incomingChatId && incomingChatId !== this.joinedChatId) {
      void this.connectSupportChat(incomingChatId, {
        loadHistory: false,
        chatType: message.chat_type,
        fromSocket: true,
      });
      return;
    }

    if (
      (message.chat_type === 'AI_BOOKING_OR_SUPPORT' ||
        this.isTerminalChatSocketStatus(message.status)) &&
      this.isSocketMetaOnlyMessage(message)
    ) {
      this.completePendingAiHandoff();
    }
  }

  private isSocketMetaOnlyMessage(message: receive): boolean {
    const hasContent =
      !!message.content?.trim() ||
      !!message.media?.length ||
      hasAiChatOptions(message.options) ||
      message.id !== undefined;

    return !hasContent && (!!message.chat_id || !!message.chat_type);
  }

  private appendSocketMessage(message: receive): void {
    const mappedMessage = this.mapHistoryMessage(message);

    // Replace any optimistic copy of our own message with the server echo.
    if (mappedMessage.sender && this.replaceOptimisticMessage(mappedMessage)) {
      return;
    }

    // During handoff, skip extra bot socket messages — closure is shown first,
    // then the welcome comes from the AI handoff API (avoids duplicate text).
    if (
      mappedMessage.role === 'ai' &&
      (this.aiHandoffRefreshInFlight ||
        (this.pendingAiHandoff && this.handoffClosureDelivered))
    ) {
      this.trackLoadedMessage(mappedMessage);
      return;
    }

    if (!this.trackLoadedMessage(mappedMessage)) {
      return;
    }

    const shouldSpeak =
      this.shouldSpeakMessageRole(mappedMessage.role) &&
      this.hasSpeakableMessageContent(mappedMessage);
    const isHandoffClosure = this.pendingAiHandoff && shouldSpeak;
    const usesTypewriter = mappedMessage.role === 'ai';

    if (shouldSpeak && usesTypewriter && (mappedMessage.message || '').trim()) {
      mappedMessage.displayedMessage = '';
    }

    if (mappedMessage.role === 'agent') {
      this.updateActiveAgentFromMessage(mappedMessage);
    }

    if (shouldSpeak) {
      if (isHandoffClosure) {
        this.handoffClosureDelivered = true;
      }

      this.appendChatMessage(mappedMessage, {
        speak: true,
        onComplete: isHandoffClosure
          ? () => this.completePendingAiHandoff()
          : undefined,
      });
      return;
    }

    this.appendChatMessage(mappedMessage, { speak: false });

    if (
      this.pendingAiHandoff &&
      (message.chat_type === 'AI_BOOKING_OR_SUPPORT' ||
        this.isTerminalChatSocketStatus(message.status))
    ) {
      this.handoffClosureDelivered = true;
      this.completePendingAiHandoff();
    }
  }

  private teardownSocketListener(): void {
    this.socketMessageSub?.unsubscribe();
    this.socketMessageSub = null;
  }

  private leaveJoinedSocketRoom(): void {
    this.teardownSocketListener();
    if (this.joinedChatId) {
      const payload: any = {
        chat_id: this.joinedChatId,
      };
      if (this.intent && this.intent === 'BOOKING') {
        payload.status = 'CLOSED';
      }
      this._chatSocketService.leaveChat(payload);
    }
    this.joinedChatId = '';
  }

  private leaveActiveChat(): void {
    this.leaveJoinedSocketRoom();
    this.activeChatId = '';
    this.continuingTicketResume = false;
    this.chatMode.set('AI');
    this.activeAgentName.set('');
    this.activeAgentId.set('');
  }

  private async refreshAssistantFromInitial(): Promise<void> {
    // Tear down the resolved support session before the initial AI flow loads.
    this.bootstrapAborted = false;
    this.cancelScheduledScroll();
    this.leaveActiveChat();
    this.clearMessagesForAiHandoff();
    this.messageControl.setValue('');
    this.navigation = AI_START_PAYLOAD.navigation;
    this.step = AI_START_PAYLOAD.step;
    this.isLoadingMessages.set(false);
    this.isTypingBotResponse.set(false);
    this.isUploadingMedia.set(false);
    this.suppressAutoScroll = false;
    await this.bootstrapAssistantAfterHandoff();
  }

  /** Re-starts the AI welcome flow after support hands the chat back. */
  private async bootstrapAssistantAfterHandoff(): Promise<void> {
    this.loader.set(true);
    this.openTtsAttempted = false;
    const lang = localStorage.getItem('language') || 'en';
    this.langugae.set(lang);

    try {
      await this.translate.ensureLoaded(lang);
      if (this.bootstrapAborted) {
        return;
      }

      this.synthesisVoices = await waitForSpeechSynthesisVoices();
      if (this.bootstrapAborted) {
        return;
      }

      if (this.malayalamTts.shouldUseServerTts(lang)) {
        void this.malayalamTts.preload();
      } else if (this.malayalamTts.isMalayalamLanguage(lang)) {
        void this.malayalamPiperTts.preload();
      }

      const welcomePayload = await this.fetchHandoffWelcomeMessage();
      if (this.bootstrapAborted) {
        return;
      }

      if (this.malayalamTts.isMalayalamLanguage(lang) && welcomePayload) {
        await this.prefetchMalayalamWelcomeSpeech(welcomePayload);
      }

      if (welcomePayload) {
        this.initMessage(welcomePayload.message, welcomePayload.options);
      } else {
        await this.speakLastConversationMessageOnOpen();
      }
    } finally {
      if (
        !this.bootstrapAborted &&
        !this.malayalamTts.isMalayalamLanguage(lang)
      ) {
        this.loader.set(false);
      }
    }
  }

  private async fetchHandoffWelcomeMessage(): Promise<InitialBotPayload | null> {
    try {
      const res = await firstValueFrom(
        this._aiService.startConversationToAi(AI_START_PAYLOAD)
      );
      const preservePayloadState = this.isSupportStepResponse(res.data);
      this.applyAgentMeta(res.data);
      const resumedTicket = await this.handleSupportChatResponse(res.data, {
        preservePayloadStateOnSupport: true,
      });

      if (resumedTicket) {
        this.appendMessagesFromApiResponse(res.data, {
          speakChat: false,
          speakStatus: false,
          statusOnly: true,
        });
        return null;
      }

      const { chatMessage } = this.appendMessagesFromApiResponse(res.data, {
        deferChatAppend: true,
      });

      if (chatMessage) {
        if (!preservePayloadState) {
          this.updateAiPayloadState(res.data);
        }
        return {
          message: chatMessage.message,
          options: chatMessage.options,
        };
      }

      if (!preservePayloadState) {
        this.updateAiPayloadState(res.data);
      }
    } catch (error) {
      console.warn('AI handoff start conversation failed', error);
    }

    return {
      message: this.resolveTranslation('AIPrompt', AIPROMPT_FALLBACK_EN),
    };
  }

  initMessage(message: string, options?: AiChatOption[]): void {
    const botMessage = this.createBotMessage(message, options);
    this.appendChatMessage(botMessage, { speak: true });
  }

  /**
   * On chat open with existing history (continuing ticket), replay the latest
   * bot/agent/system message with the same synced typewriter + TTS used for
   * live replies (word-by-word, not full text upfront).
   */
  private findLastSpeakableConversationMessage(): AiChatMessage | null {
    const visible = this.messagesData().filter(item =>
      this.shouldShowChatMessage(item)
    );
    for (let index = visible.length - 1; index >= 0; index--) {
      const message = visible[index];
      if (
        this.shouldSpeakMessageRole(message.role) &&
        !!(message.message || '').trim()
      ) {
        return message;
      }
    }
    return null;
  }

  private async speakLastConversationMessageOnOpen(): Promise<void> {
    if (
      !this.platformBrowser ||
      this.bootstrapAborted ||
      this.openTtsAttempted
    ) {
      return;
    }

    if (this.isMuted()) {
      // Nothing is spoken — settle so hands-free starts listening.
      this.voiceAssistant.notifyPresentationSettled();
      return;
    }

    // Claim immediately so bootstrap + history-load call sites don't double-speak.
    this.openTtsAttempted = true;

    // Wait briefly if history is still settling into the signal.
    let message = this.findLastSpeakableConversationMessage();
    if (!message) {
      for (let attempt = 0; attempt < 8 && !this.bootstrapAborted; attempt++) {
        await new Promise<void>(resolve => setTimeout(resolve, 50));
        message = this.findLastSpeakableConversationMessage();
        if (message) {
          break;
        }
      }
    }

    if (!message || this.bootstrapAborted || this.isMuted()) {
      // Allow the other call site to retry if history was not ready yet.
      if (!message && this.messagesData().length === 0) {
        this.openTtsAttempted = false;
      } else if (!this.bootstrapAborted) {
        // Nothing to speak — settle so hands-free starts listening.
        this.voiceAssistant.notifyPresentationSettled();
      }
      this.logger.warn(
        '[AI Chat] Open TTS skipped — no speakable last message'
      );
      return;
    }

    // Clear any status/presentation TTS race from the start-conversation payload.
    this.flushPresentationQueueOnInterrupt();
    this.stopActiveSpeechAudio();

    // Reset display so animateAndSpeakMessage can type word-by-word in sync with TTS.
    message.displayedMessage = '';
    if (message.media?.length) {
      message.mediaRevealed = false;
    }

    this.loader.set(false);
    this.cd.detectChanges();
    this.scheduleScrollToBottom();

    this.logger.warn(
      '[AI Chat] Open TTS — synced typewriter for last message',
      {
        id: message.id,
        role: message.role,
        preview: (message.message || '').slice(0, 80),
      }
    );

    try {
      await new Promise<void>(resolve => {
        void this.animateAndSpeakMessage(message, () => {
          this.voiceAssistant.notifyPresentationSettled();
          resolve();
        });
      });
    } catch (error) {
      this.logger.warn(
        'Failed to speak last conversation message on open',
        error
      );
      message.displayedMessage = message.message;
      this.revealMessageMedia(message);
      this.isTypingBotResponse.set(false);
      this.loader.set(false);
      this.voiceAssistant.notifyPresentationSettled();
      this.cd.detectChanges();
    }
  }

  async send(
    pMessage?: string,
    options?: { fromVoice?: boolean }
  ): Promise<void> {
    if (this.isUploadingMedia()) {
      return;
    }

    // Mic transcripts are emitted as recognition ends; do not block on isListening()/loader
    // guards that race with short utterances ("1", "3", etc.).
    if (options?.fromVoice) {
      if (this.isAwaitingBotResponse() || this.isLoadingMessages()) {
        return;
      }
    } else if (this.isListening() || this.isInputDisabled()) {
      return;
    }

    const msg = this.messageControl.value;
    const message = pMessage || msg;

    if (message && message.trim() !== '') {
      this.idleTimer.reset();
      this.interruptAssistantSpeech();
      this.voiceAssistant.notifyAiRequestStarted();

      if (this.chatMode() === 'HYBRID') {
        this.sendHybridMessage(message.trim());
        return;
      }

      this.loader.set(true);
      this.apiResponseReceived = false;
      this.isAwaitingBotResponse.set(true);
      const outgoingStep = this.getOutgoingAiStep();
      const payload: any = {
        prompt: message,
        navigation: this.navigation,
        step: outgoingStep,
      };

      if (payload['prompt'] === ' ') {
        delete payload['prompt'];
      }

      this.appendChatMessage(
        {
          message: message,
          sender: true,
          role: 'user',
          created_at: new Date().toISOString(),
        },
        { speak: false, revealImmediately: true }
      );
      // Clear input
      this.messageControl.setValue('');

      this._aiService
        .SendMessageToAi(payload)
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe({
          next: (res: AIResponse) => {
            void this.handleAiConversationResponse(res.data);
          },
          error: (error: unknown) => {
            this.logger.error('AI message request failed', error);
            this.releaseInputAfterBotResponse();
            this.voiceAssistant.notifyTurnFailed();
          },
        });
    } else {
      this.toaster.showError(
        this.translate.translate('enterMessagetoContinue')
      );
    }
  }

  private async handleAiConversationResponse(
    data: AIResponse['data']
  ): Promise<void> {
    const skippedBootstrapMessage = await this.handleSupportChatResponse(data);
    this.applyAgentMeta(data);
    this.intent = data.intent;
    if (data.next_step !== 'SUCCESS') {
      this.apiResponseReceived = true;

      if (!skippedBootstrapMessage) {
        this.appendMessagesFromApiResponse(data, { speakChat: true });
        this.updateAiPayloadState(data);
      } else {
        this.appendMessagesFromApiResponse(data, {
          speakChat: false,
          statusOnly: true,
        });
        this.updateAiPayloadState(data);
      }
      this.messageControl.setValue('');
      this.tryCompleteAiSendTurn();
    } else {
      this.releaseInputAfterBotResponse();
      this.closeAI();
    }
  }

  private sendHybridMessage(message: string): void {
    if (!this.activeChatId) {
      this.toaster.showError(
        this.translate.translate('enterMessagetoContinue')
      );
      return;
    }

    this.interruptAssistantSpeech();

    const payload: sendMessage = {
      chat_id: this.activeChatId,
      content: message,
      type: 'TEXT',
    };
    this._chatSocketService.sendMessage(payload);
    this.addOptimisticTextMessage(message);
    this.messageControl.setValue('');
  }

  private addOptimisticTextMessage(content: string): void {
    const optimisticMessage: AiChatMessage = {
      message: content,
      displayedMessage: content,
      sender: true,
      role: 'user',
      type: 'TEXT',
      created_at: new Date().toISOString(),
      sender_photo: this.userProfile?.photo ?? null,
      isRevealed: true,
    };
    this.messagesData.update(prev => [...prev, optimisticMessage]);
    this.scheduleScrollToBottom();
  }

  /** Uploads selected images/PDF files and shares them over the hybrid chat socket. */
  async onMediaSelected(event: Event): Promise<void> {
    if (
      this.chatMode() !== 'HYBRID' ||
      !this.activeChatId ||
      this.isUploadingMedia()
    ) {
      return;
    }

    this.voiceAssistant.stopHandsFree();
    this.idleTimer.reset();
    const result = await this._uploadImageService.onChatMediaUpload(event);
    if (!Array.isArray(result)) {
      return;
    }

    const media = result
      .filter((item: { data?: string }) => !!item?.data)
      .map((item: { data: string }) => item.data);

    await this.publishHybridMediaUpload(media);
  }

  /** Opens the camera when available; otherwise shows a toaster message. */
  async onCameraClick(): Promise<void> {
    if (
      this.isInputDisabled() ||
      this.chatMode() !== 'HYBRID' ||
      !this.activeChatId ||
      this.isUploadingMedia()
    ) {
      return;
    }
    this.voiceAssistant.stopHandsFree();

    if (!this.isCameraApiSupported()) {
      this.toaster.showError(this.translate.translate('cameraNotAvailable'));
      return;
    }

    const cameraAvailable = await this.isCameraDeviceAvailable();
    if (!cameraAvailable) {
      this.toaster.showError(this.translate.translate('cameraNotAvailable'));
      return;
    }

    this.openCameraDialog();
  }

  private isCameraApiSupported(): boolean {
    return (
      this.platformBrowser &&
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia
    );
  }

  private async isCameraDeviceAvailable(): Promise<boolean> {
    if (!this.isCameraApiSupported()) {
      return false;
    }

    try {
      if (navigator.mediaDevices.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const hasVideoInput = devices.some(
          device => device.kind === 'videoinput'
        );
        if (hasVideoInput) {
          return true;
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch {
      return false;
    }
  }

  private openCameraDialog(): void {
    void import('../../../modals/camera/camera.component').then(module => {
      this.closeCameraDialogIfOpen();
      this.cameraDialogRef = this.dialogService.open(module.CameraComponent, {
        header: this.translate.translate('takePhoto'),
        modal: true,
        baseZIndex: 10000000001,
        focusOnShow: false,
      });

      this.cameraDialogRef.onClose
        .pipe(takeUntilDestroyed(this.destroy))
        .subscribe((res: { file?: File } | undefined) => {
          this.cameraDialogRef = null;
          if (res?.file) {
            void this.uploadCameraCapture(res.file);
          }
        });
    });
  }

  private closeCameraDialogIfOpen(): void {
    if (!this.cameraDialogRef) {
      return;
    }

    this.cameraDialogRef.close();
    this.cameraDialogRef = null;
  }

  private isTerminalChatSocketStatus(status: string | undefined): boolean {
    const normalized = status?.trim().toUpperCase();
    return normalized === 'RESOLVED' || normalized === 'CLOSED';
  }

  private async uploadCameraCapture(file: File): Promise<void> {
    if (
      this.chatMode() !== 'HYBRID' ||
      !this.activeChatId ||
      this.isUploadingMedia()
    ) {
      return;
    }

    const result = await this._uploadImageService.uploadChatMediaFile(file);
    if (result && result.data) {
      await this.publishHybridMediaUpload([result.data]);
    }
  }

  private async publishHybridMediaUpload(media: string[]): Promise<void> {
    if (media.length === 0) {
      return;
    }

    this.isUploadingMedia.set(true);

    try {
      this.addOptimisticMediaMessage(media);
      this.sendHybridMedia(media);
      await this.waitForMediaRendered(media);
    } finally {
      this.isUploadingMedia.set(false);
    }
  }

  private addOptimisticMediaMessage(media: string[]): void {
    const optimisticMessage: AiChatMessage = {
      message: '',
      sender: true,
      role: 'user',
      media,
      type: 'MEDIA',
      created_at: new Date().toISOString(),
      sender_photo: this.userProfile?.photo ?? null,
      isRevealed: true,
    };
    this.messagesData.update(prev => [...prev, optimisticMessage]);
    this.scheduleScrollToBottom();
  }

  private replaceOptimisticMessage(mapped: AiChatMessage): boolean {
    let replaced = false;

    this.messagesData.update(prev =>
      prev.map(message => {
        if (replaced || message.id || !message.sender) {
          return message;
        }

        const sameMedia =
          !!message.media?.length &&
          !!mapped.media?.length &&
          this.isSameMediaSet(message.media, mapped.media);
        const sameText =
          !message.media?.length &&
          !mapped.media?.length &&
          !!message.message &&
          message.message === mapped.message;

        if (sameMedia || sameText) {
          replaced = true;
          this.trackLoadedMessage(mapped);
          return {
            ...message,
            ...mapped,
            created_at: mapped.created_at ?? message.created_at,
            displayedMessage:
              message.displayedMessage ||
              mapped.displayedMessage ||
              mapped.message,
          };
        }
        return message;
      })
    );

    return replaced;
  }

  private isSameMediaSet(first: string[], second: string[]): boolean {
    return (
      first.length === second.length && first.every(url => second.includes(url))
    );
  }

  private async waitForMediaRendered(media: string[]): Promise<void> {
    if (!this.platformBrowser) {
      return;
    }

    this.cd.detectChanges();
    await this.preloadMediaAssets(media);
    await new Promise<void>(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });
  }

  private preloadMediaAssets(urls: string[]): Promise<void> {
    const tasks = urls.map(url => {
      if (this.isPdf(url) || this.isVideo(url)) {
        return Promise.resolve();
      }

      return new Promise<void>(resolve => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = url;
      });
    });

    return Promise.all(tasks).then(() => undefined);
  }

  private sendHybridMedia(media: string[]): void {
    if (!this.activeChatId) {
      return;
    }

    const payload: sendMessage = {
      chat_id: this.activeChatId,
      media,
      type: 'MEDIA',
    };
    this._chatSocketService.sendMessage(payload);
    this.scheduleScrollToBottom();
  }

  isPdf(url: string): boolean {
    return /\.pdf(\?.*)?$/i.test(url ?? '');
  }

  isVideo(url: string): boolean {
    return /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(url ?? '');
  }

  isImage(url: string): boolean {
    return /\.(png|jpe?g|gif|webp|svg|bmp)(\?.*)?$/i.test(url ?? '');
  }

  isDocument(url: string): boolean {
    return this.isPdf(url) || (!this.isImage(url) && !this.isVideo(url));
  }

  isDocumentsOnlyMedia(media?: string[]): boolean {
    return !!media?.length && media.every(url => this.isDocument(url));
  }

  shouldShowMessageMedia(message: AiChatMessage): boolean {
    return !!message.media?.length && message.mediaRevealed !== false;
  }

  shouldShowHelpGuideLink(message: AiChatMessage): boolean {
    if (!message.helpGuideSlug?.trim()) {
      return false;
    }

    const messageText = (message.message || '').trim();
    if (!messageText) {
      return true;
    }

    return (message.displayedMessage || '').trim() === messageText;
  }

  navigateToHelpGuide(slug: string): void {
    const normalizedSlug = slug.trim();
    if (!normalizedSlug) {
      return;
    }

    void this.router.navigate(['/public/slug', normalizedSlug]);
  }

  shouldShowMessageOptions(message: AiChatMessage): boolean {
    if (message.role !== 'ai' || !hasAiChatOptions(message.options)) {
      return false;
    }

    const messageText = (message.message || '').trim();
    if (!messageText) {
      return true;
    }

    return (message.displayedMessage || '').trim() === messageText;
  }

  canSelectOption(message: AiChatMessage, messageIndex: number): boolean {
    if (
      message.sender ||
      message.role !== 'ai' ||
      message.optionsLocked ||
      this.isInputDisabled()
    ) {
      return false;
    }

    if (!hasAiChatOptions(message.options)) {
      return false;
    }

    return this.getLatestActionableOptionsMessageIndex() === messageIndex;
  }

  onOptionSelected(message: AiChatMessage, option: AiChatOption): void {
    if (!hasAiChatOptions(message.options)) {
      return;
    }
    this.voiceAssistant.stopHandsFree();

    const messageIndex = this.messagesData().indexOf(message);
    if (!this.canSelectOption(message, messageIndex)) {
      return;
    }

    this.messagesData.update(prev =>
      prev.map(item =>
        item === message
          ? { ...item, selectedOption: option.name, optionsLocked: true }
          : item
      )
    );
    void this.send(getOptionSubmissionValue(option));
  }

  private getLatestActionableOptionsMessageIndex(): number {
    const messages = this.messagesData();

    for (let index = messages.length - 1; index >= 0; index--) {
      const message = messages[index];
      if (
        message.role === 'ai' &&
        hasAiChatOptions(message.options) &&
        !message.optionsLocked
      ) {
        return index;
      }
    }

    return -1;
  }

  private shouldSpeakMessageRole(role?: ChatMessageRole): boolean {
    return (
      role === 'ai' || role === 'system' || role === 'info' || role === 'agent'
    );
  }

  private hasSpeakableMessageContent(message: AiChatMessage): boolean {
    return (
      !!(message.message || '').trim() || hasAiChatOptions(message.options)
    );
  }

  async animateAndSpeakMessage(
    messageObj: AiChatMessage,
    onComplete?: () => void
  ): Promise<void> {
    if (
      !this.platformBrowser ||
      !this.shouldSpeakMessageRole(messageObj.role)
    ) {
      onComplete?.();
      return;
    }

    this.voiceAssistant.notifyTtsStarted();
    const sessionId = ++this.speechSessionId;
    this.activeSpeechMessage = messageObj;
    this.isTypingBotResponse.set(true);

    const hasMessageText = !!(messageObj.message || '').trim();
    const chatOptions = normalizeAiChatOptions(messageObj.options);
    const usesTypewriter = messageObj.role === 'ai';

    if (messageObj.media?.length) {
      messageObj.mediaRevealed = false;
    }

    const runOnComplete = (): void => {
      if (sessionId !== this.speechSessionId) {
        return;
      }

      this.isTypingBotResponse.set(false);
      this.loader.set(false);
      this.currentUtterance = null;
      this.activeSpeechMessage = null;
      this.cd.detectChanges();
      this.scheduleScrollToBottom();
      onComplete?.();
    };

    if (!hasMessageText && chatOptions.length === 0) {
      this.revealMessageMedia(messageObj);
      runOnComplete();
      return;
    }

    const hasSpeech = isSpeechSynthesisSupported();

    if (!usesTypewriter) {
      if (!hasSpeech) {
        this.revealMessageMedia(messageObj);
        runOnComplete();
        return;
      }

      try {
        if (hasMessageText) {
          const speakText =
            messageObj.speakableMessage ??
            getSpeakableAiMessageText(messageObj.message);
          await this.speakUtteranceText(speakText, sessionId);
        }

        if (sessionId !== this.speechSessionId) {
          return;
        }

        this.revealMessageMedia(messageObj);
        runOnComplete();
      } catch (event) {
        if (sessionId !== this.speechSessionId) {
          return;
        }

        console.error('Speech synthesis error', event);
        this.revealMessageMedia(messageObj);
        runOnComplete();
      }

      return;
    }

    if (hasMessageText) {
      messageObj.displayedMessage = '';
    }

    if (!hasSpeech) {
      if (hasMessageText) {
        messageObj.displayedMessage = messageObj.message;
        this.cd.detectChanges();
      }

      this.revealMessageMedia(messageObj);

      for (let index = 0; index < chatOptions.length; index++) {
        if (sessionId !== this.speechSessionId) {
          return;
        }

        await this.speakUtteranceText(
          buildOptionSpeakText(chatOptions[index]),
          sessionId
        );
      }

      runOnComplete();
      return;
    }

    try {
      if (hasMessageText) {
        const speakText =
          messageObj.speakableMessage ??
          getSpeakableAiMessageText(messageObj.message);
        await this.speakUtteranceWithSyncedDisplay(
          messageObj,
          speakText,
          sessionId
        );

        if (sessionId !== this.speechSessionId) {
          return;
        }
      }

      this.revealMessageMedia(messageObj);

      for (let index = 0; index < chatOptions.length; index++) {
        if (sessionId !== this.speechSessionId) {
          return;
        }

        await this.speakUtteranceText(
          buildOptionSpeakText(chatOptions[index]),
          sessionId
        );
      }

      runOnComplete();
    } catch (event) {
      if (sessionId !== this.speechSessionId) {
        return;
      }

      console.error('Speech synthesis error', event);
      if (hasMessageText) {
        messageObj.displayedMessage = messageObj.message;
      }
      this.revealMessageMedia(messageObj);
      runOnComplete();
    }
  }

  private revealMessageMedia(messageObj: AiChatMessage): void {
    if (!messageObj.media?.length || messageObj.mediaRevealed !== false) {
      return;
    }

    messageObj.mediaRevealed = true;
    this.cd.detectChanges();
    this.scheduleScrollToBottom();
  }

  private clearDisplaySyncInterval(): void {
    if (this.displaySyncInterval) {
      clearInterval(this.displaySyncInterval);
      this.displaySyncInterval = null;
    }
  }

  /**
   * Reveals message text in sync with speech synthesis boundary events.
   * Falls back to duration-based progress when word boundaries are unavailable.
   */
  private speakUtteranceWithSyncedDisplay(
    messageObj: AiChatMessage,
    speakText: string,
    sessionId: number
  ): Promise<void> {
    const fullDisplay = messageObj.message;
    messageObj.displayedMessage = '';

    if (!speakText.trim()) {
      messageObj.displayedMessage = fullDisplay;
      this.cd.detectChanges();
      return Promise.resolve();
    }

    if (this.isMuted()) {
      return this.speakDisplayOnlySync(
        messageObj,
        speakText,
        sessionId,
        fullDisplay
      );
    }

    if (this.malayalamTts.isMalayalamLanguage(this.langugae())) {
      return this.speakMalayalamWithSyncedDisplay(
        messageObj,
        speakText,
        sessionId,
        fullDisplay
      );
    }

    return this.speakUtteranceWithSyncedDisplaySpeechSynthesis(
      messageObj,
      speakText,
      sessionId,
      fullDisplay
    );
  }

  /** Typewriter-only reveal when TTS is muted — no audio playback. */
  private speakDisplayOnlySync(
    messageObj: AiChatMessage,
    speakText: string,
    sessionId: number,
    fullDisplay: string
  ): Promise<void> {
    messageObj.displayedMessage = '';
    this.cd.detectChanges();

    const useTokenSync = this.malayalamTts.isMalayalamLanguage(this.langugae());
    const tokens = useTokenSync
      ? this.splitMalayalamDisplayTokens(fullDisplay)
      : null;

    const setDisplayProgress = (ratio: number): void => {
      if (sessionId !== this.speechSessionId) {
        return;
      }

      const clampedRatio = Math.min(1, Math.max(0, ratio));

      if (tokens) {
        const tokenCount =
          clampedRatio >= 1
            ? tokens.length
            : Math.max(0, Math.floor(clampedRatio * tokens.length));
        messageObj.displayedMessage =
          tokenCount >= tokens.length
            ? fullDisplay
            : tokens.slice(0, tokenCount).join('');
      } else {
        const targetLength =
          clampedRatio >= 1
            ? fullDisplay.length
            : Math.ceil(clampedRatio * fullDisplay.length);
        messageObj.displayedMessage = fullDisplay.slice(0, targetLength);
      }

      this.cd.detectChanges();
      this.scheduleScrollToBottom();
    };

    const wordCount = speakText.trim().split(/\s+/).filter(Boolean).length;
    const estimatedMs = Math.max(1200, (wordCount / 130) * 60000);
    const startedAt = Date.now();

    return new Promise<void>(resolve => {
      this.clearDisplaySyncInterval();
      this.displaySyncInterval = setInterval(() => {
        if (sessionId !== this.speechSessionId) {
          this.clearDisplaySyncInterval();
          resolve();
          return;
        }

        const ratio = Math.min(1, (Date.now() - startedAt) / estimatedMs);
        setDisplayProgress(ratio);

        if (ratio >= 1) {
          messageObj.displayedMessage = fullDisplay;
          this.clearDisplaySyncInterval();
          this.cd.detectChanges();
          this.scheduleScrollToBottom();
          resolve();
        }
      }, 40);
    });
  }

  /** Malayalam-only: typewriter synced to server TTS audio playback (no estimated drift). */
  private speakMalayalamWithSyncedDisplay(
    messageObj: AiChatMessage,
    speakText: string,
    sessionId: number,
    fullDisplay: string
  ): Promise<void> {
    const tokens = this.splitMalayalamDisplayTokens(fullDisplay);
    let lastRenderedLength = -1;
    let lastScrollAt = 0;

    const setDisplayProgress = (ratio: number): void => {
      if (sessionId !== this.speechSessionId) {
        return;
      }

      const clampedRatio = Math.min(1, Math.max(0, ratio));
      const tokenCount =
        clampedRatio >= 1
          ? tokens.length
          : Math.max(0, Math.floor(clampedRatio * tokens.length));
      const next =
        tokenCount >= tokens.length
          ? fullDisplay
          : tokens.slice(0, tokenCount).join('');

      if (next.length === lastRenderedLength) {
        return;
      }

      lastRenderedLength = next.length;
      messageObj.displayedMessage = next;
      this.cd.detectChanges();

      const now = Date.now();
      if (now - lastScrollAt > 120) {
        lastScrollAt = now;
        this.scheduleScrollToBottom();
      }
    };

    return this.speakMalayalamWithTimeout(speakText, sessionId, {
      onPreparing: () => {
        if (sessionId !== this.speechSessionId) {
          return;
        }

        messageObj.displayedMessage = '';
        this.loader.set(true);
        this.cd.detectChanges();
      },
      onStart: () => {
        if (sessionId !== this.speechSessionId) {
          return;
        }

        lastRenderedLength = -1;
        this.loader.set(false);
        setDisplayProgress(0);
      },
      onProgress: ratio => setDisplayProgress(ratio),
    })
      .then(() => {
        if (sessionId !== this.speechSessionId) {
          return;
        }

        messageObj.displayedMessage = fullDisplay;
        this.loader.set(false);
        this.cd.detectChanges();
        this.scheduleScrollToBottom();
      })
      .catch(error => {
        if (sessionId !== this.speechSessionId || this.isMuted()) {
          return Promise.resolve();
        }

        this.loader.set(false);
        this.logger.warn(
          'Malayalam TTS unavailable, falling back to SpeechSynthesis',
          error
        );

        return this.speakUtteranceWithSyncedDisplaySpeechSynthesis(
          messageObj,
          speakText,
          sessionId,
          fullDisplay
        );
      });
  }

  /** Word chunks for smoother Malayalam typewriter sync with speech. */
  private splitMalayalamDisplayTokens(text: string): string[] {
    return text.match(/\S+\s*/gu) ?? [text];
  }

  private speakUtteranceWithSyncedDisplaySpeechSynthesis(
    messageObj: AiChatMessage,
    speakText: string,
    sessionId: number,
    fullDisplay: string
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let boundarySeen = false;
      let stopEstimatedSync: (() => void) | null = null;

      const setDisplayProgress = (ratio: number): void => {
        if (sessionId !== this.speechSessionId) {
          return;
        }

        const clampedRatio = Math.min(1, Math.max(0, ratio));
        const targetLength =
          clampedRatio >= 1
            ? fullDisplay.length
            : Math.ceil(clampedRatio * fullDisplay.length);
        messageObj.displayedMessage = fullDisplay.slice(0, targetLength);
        this.cd.detectChanges();
        this.scheduleScrollToBottom();
      };

      const utterance = new SpeechSynthesisUtterance(speakText);
      utterance.rate = 0.92;
      utterance.volume = 1;
      this.currentUtterance = utterance;

      let finished = false;
      const finishSpeech = (): void => {
        if (finished) {
          return;
        }
        finished = true;
        stopEstimatedSync?.();
        stopEstimatedSync = null;
        this.clearDisplaySyncInterval();

        if (sessionId !== this.speechSessionId) {
          resolve();
          return;
        }

        messageObj.displayedMessage = fullDisplay;
        this.currentUtterance = null;
        this.cd.detectChanges();
        this.scheduleScrollToBottom();
        resolve();
      };

      utterance.onstart = () => {
        if (sessionId !== this.speechSessionId) {
          return;
        }

        stopEstimatedSync = this.startEstimatedDisplaySync(
          speakText,
          ratio => setDisplayProgress(ratio),
          sessionId,
          () => boundarySeen
        );
      };

      utterance.onboundary = (event: SpeechSynthesisEvent) => {
        if (sessionId !== this.speechSessionId) {
          return;
        }

        boundarySeen = true;
        stopEstimatedSync?.();
        stopEstimatedSync = null;
        this.clearDisplaySyncInterval();

        const spokenIndex = event.charIndex + (event.charLength ?? 0);
        setDisplayProgress(spokenIndex / Math.max(speakText.length, 1));
      };

      utterance.onend = () => {
        finishSpeech();
      };

      utterance.onerror = event => {
        stopEstimatedSync?.();
        stopEstimatedSync = null;
        this.clearDisplaySyncInterval();

        if (sessionId !== this.speechSessionId || this.isMuted()) {
          finishSpeech();
          return;
        }

        this.currentUtterance = null;
        const errorName = (event as SpeechSynthesisErrorEvent).error;
        if (errorName === 'interrupted' || errorName === 'canceled') {
          finishSpeech();
          return;
        }
        reject(event);
      };

      if (this.isMuted()) {
        finishSpeech();
        return;
      }

      void this.playSpeechUtterance(utterance).then(started => {
        if (!started) {
          finishSpeech();
        }
      });
    });
  }

  private startEstimatedDisplaySync(
    speakText: string,
    onProgress: (ratio: number) => void,
    sessionId: number,
    hasBoundarySync: () => boolean
  ): () => void {
    this.clearDisplaySyncInterval();

    const wordCount = speakText.trim().split(/\s+/).filter(Boolean).length;
    const estimatedMs = Math.max(1200, (wordCount / 130) * 60000);
    const startedAt = Date.now();

    this.displaySyncInterval = setInterval(() => {
      if (sessionId !== this.speechSessionId || hasBoundarySync()) {
        this.clearDisplaySyncInterval();
        return;
      }

      const ratio = Math.min(1, (Date.now() - startedAt) / estimatedMs);
      onProgress(ratio);

      if (ratio >= 1) {
        this.clearDisplaySyncInterval();
      }
    }, 40);

    return () => this.clearDisplaySyncInterval();
  }

  private speakUtteranceText(text: string, sessionId: number): Promise<void> {
    if (!text.trim() || sessionId !== this.speechSessionId || this.isMuted()) {
      return Promise.resolve();
    }

    if (this.malayalamTts.isMalayalamLanguage(this.langugae())) {
      return this.speakMalayalamText(text, sessionId);
    }

    return this.speakUtteranceTextWithSpeechSynthesis(text, sessionId);
  }

  /** Malayalam: server Anjali when enabled, otherwise Piper meera (female). */
  private speakMalayalamText(text: string, sessionId: number): Promise<void> {
    return this.speakMalayalamWithTimeout(text, sessionId, {
      onPreparing: () => {
        if (sessionId === this.speechSessionId) {
          this.loader.set(true);
        }
      },
      onStart: () => {
        if (sessionId === this.speechSessionId) {
          this.loader.set(false);
        }
      },
    }).catch(error => {
      if (sessionId !== this.speechSessionId || this.isMuted()) {
        return Promise.resolve();
      }

      this.logger.warn(
        'Malayalam TTS unavailable, falling back to SpeechSynthesis',
        error
      );
      return this.speakUtteranceTextWithSpeechSynthesis(text, sessionId);
    });
  }

  private speakMalayalamWithTimeout(
    text: string,
    sessionId: number,
    options: MalayalamTtsSpeakOptions = {}
  ): Promise<void> {
    if (this.isMuted() || sessionId !== this.speechSessionId) {
      return Promise.resolve();
    }

    const tts = this.malayalamTts.shouldUseServerTts(this.langugae())
      ? this.malayalamTts
      : this.malayalamPiperTts;

    return tts.speak(text, options).then(() => {
      if (sessionId !== this.speechSessionId) {
        return;
      }
    });
  }

  private speakUtteranceTextWithSpeechSynthesis(
    text: string,
    sessionId: number
  ): Promise<void> {
    if (this.isMuted()) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.92;
      utterance.volume = 1;
      this.currentUtterance = utterance;

      let finished = false;
      const finishSpeech = (): void => {
        if (finished) {
          return;
        }
        finished = true;

        if (sessionId !== this.speechSessionId) {
          resolve();
          return;
        }

        this.currentUtterance = null;
        resolve();
      };

      utterance.onend = () => {
        finishSpeech();
      };

      utterance.onerror = event => {
        if (sessionId !== this.speechSessionId || this.isMuted()) {
          resolve();
          return;
        }

        this.currentUtterance = null;
        // Chrome often reports "interrupted"/"canceled" after cancel()+speak — treat as done.
        const errorName = (event as SpeechSynthesisErrorEvent).error;
        if (errorName === 'interrupted' || errorName === 'canceled') {
          resolve();
          return;
        }
        reject(event);
      };

      void this.playSpeechUtterance(utterance).then(started => {
        if (!started) {
          finishSpeech();
        }
      });
    });
  }

  private async playSpeechUtterance(
    utterance: SpeechSynthesisUtterance
  ): Promise<boolean> {
    if (!isSpeechSynthesisSupported() || this.isMuted()) {
      return false;
    }

    const voices = await waitForSpeechSynthesisVoices();
    if (voices.length > 0) {
      this.synthesisVoices = voices;
    }
    this.applySpeechVoice(utterance);

    if (this.bootstrapAborted || this.isMuted()) {
      return false;
    }

    const synth = window.speechSynthesis;
    if (synth.speaking || synth.pending) {
      synth.cancel();
    }

    await deferSpeechStart();

    if (this.bootstrapAborted || this.isMuted()) {
      return false;
    }

    synth.resume();
    // Spoken text only — e.g. "Logibids" is said "Lojibids"; the chat text is unchanged.
    utterance.text = applySpeechPronunciations(utterance.text);
    synth.speak(utterance);

    this.stopSpeechKeepAlive?.();
    this.stopSpeechKeepAlive = needsSpeechKeepAlive(utterance.voice)
      ? startSpeechKeepAlive()
      : null;
    return true;
  }

  private startManualAnimation(
    messageObj: {
      message: string;
      displayedMessage?: string;
    },
    completeBusyState = true,
    onComplete?: () => void,
    sessionId?: number
  ): void {
    if (this.typingAnimationInterval) {
      clearInterval(this.typingAnimationInterval);
      this.typingAnimationInterval = null;
    }

    const words = messageObj.message.split(' ');
    let index = 0;
    this.typingAnimationInterval = setInterval(() => {
      if (sessionId !== undefined && sessionId !== this.speechSessionId) {
        clearInterval(this.typingAnimationInterval!);
        this.typingAnimationInterval = null;
        return;
      }

      if (index < words.length) {
        messageObj.displayedMessage += (index === 0 ? '' : ' ') + words[index];
        index++;
        this.cd.detectChanges();
        this.scheduleScrollToBottom();
      } else {
        clearInterval(this.typingAnimationInterval!);
        this.typingAnimationInterval = null;
        if (completeBusyState) {
          this.isTypingBotResponse.set(false);
          this.loader.set(false);
        }
        this.scheduleScrollToBottom();
        onComplete?.();
      }
    }, 150);
  }

  onUserTyping(): void {
    this.idleTimer.reset();
  }

  /** Focusing the text box ends hands-free; the mic button starts it again. */
  onComposerFocus(): void {
    this.voiceAssistant.stopHandsFree();
  }

  private armIdleTimerForLatestReply(messages: AiChatMessage[]): void {
    let latestReply: AiChatMessage | null = null;
    for (let index = messages.length - 1; index >= 0; index--) {
      const message = messages[index];
      if (message.sender || message.role === 'user') {
        break;
      }
      if (message.role === 'ai' && message.isRevealed !== false) {
        latestReply = message;
        break;
      }
    }

    if (!latestReply || latestReply === this.idleArmedMessage) {
      return;
    }
    this.idleArmedMessage = latestReply;
    this.idleTimer.start(this.activeChatId);
  }

  private showIdleFollowUp(followUp: AiConvoIdleMessage): void {
    const botMessage: AiChatMessage = {
      ...this.createBotMessage(followUp.content),
      id: followUp.id,
      created_at: followUp.created_at,
    };
    // The follow-up must not start a new idle period; the close timer is running.
    this.idleArmedMessage = botMessage;
    this.appendChatMessage(botMessage, { speak: true });
  }

  /** Server closed the chat after inactivity: wipe it so the next open calls /start. */
  private clearChatAfterIdleClose(): void {
    this.leaveActiveChat();
    this.clearMessagesForAiHandoff();
    this.idleArmedMessage = null;
    this.messageControl.setValue('');
    this.navigation = AI_START_PAYLOAD.navigation;
    this.step = AI_START_PAYLOAD.step;
    this.closeAI();
  }

  closeAI(): void {
    this.bootstrapAborted = true;
    this.interruptAssistantSpeech();
    this.leaveActiveChat();
    this.voiceAssistant.notifyChatClosed();
    this._aiService.setData(false);
  }

  async onMicClick(): Promise<void> {
    if (!this.platformBrowser || this.isUploadingMedia()) {
      return;
    }

    if (this.loader() && !this.isTypingBotResponse() && !this.isListening()) {
      return;
    }

    this.idleTimer.reset();
    this.interruptAssistantSpeech();
    this.syncSpeechLanguage();
    await this.voiceAssistant.toggleConversationListen(this.getSpeechLang());
    this.loader.set(false);
  }

  // Global mute: stop active speech immediately and block future TTS until unmuted.
  toggleMute(): void {
    const newMuteState = !this.isMuted();
    this.isMuted.set(newMuteState);

    if (newMuteState) {
      this.speechSessionId++;
      this.stopActiveSpeechAudio();
      this.clearDisplaySyncInterval();

      if (this.activeSpeechMessage) {
        this.activeSpeechMessage.displayedMessage =
          this.activeSpeechMessage.message;
        this.revealMessageMedia(this.activeSpeechMessage);
        this.activeSpeechMessage = null;
      }

      this.isTypingBotResponse.set(false);
      this.loader.set(false);
      this.cd.detectChanges();
    }
  }

  /** Stops current TTS playback without interrupting message text flow. */
  private stopActiveSpeechAudio(): void {
    if (this.malayalamTts.isMalayalamLanguage(this.langugae())) {
      this.malayalamTts.stop();
      this.malayalamPiperTts.stop();
    }

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    this.currentUtterance = null;
    this.loader.set(false);
  }

  private observeChatContentResize(): void {
    const container = this.chatContainer?.nativeElement;
    if (!container) {
      return;
    }

    const content =
      container.querySelector<HTMLElement>('.user_msg') ?? container;

    this.chatResizeObserver?.disconnect();
    this.chatResizeObserver = new ResizeObserver(() => {
      if (!this.suppressAutoScroll && this.isChatNearBottom()) {
        this.scrollChatToBottom();
      }
    });
    this.chatResizeObserver.observe(content);
  }

  /** Shows grab/grabbing cursor feedback while the chat is being scrolled. */
  private bindChatScrollCursor(): void {
    this.unbindChatScrollCursor();

    const el = this.chatContainer?.nativeElement;
    if (!el) {
      return;
    }

    const markScrolling = (): void => {
      el.classList.add('is-scrolling');

      if (this.scrollCursorTimeout) {
        clearTimeout(this.scrollCursorTimeout);
      }

      this.scrollCursorTimeout = setTimeout(() => {
        el.classList.remove('is-scrolling');
        this.scrollCursorTimeout = null;
      }, 350);
    };

    const onMouseDown = (): void => {
      el.classList.add('is-grabbing');
    };

    const onMouseUp = (): void => {
      el.classList.remove('is-grabbing');
    };

    el.addEventListener('wheel', markScrolling, { passive: true });
    el.addEventListener('scroll', markScrolling, { passive: true });
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mouseleave', onMouseUp);

    this.chatScrollCursorCleanup = () => {
      el.removeEventListener('wheel', markScrolling);
      el.removeEventListener('scroll', markScrolling);
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mouseleave', onMouseUp);
      el.classList.remove('is-scrolling', 'is-grabbing');

      if (this.scrollCursorTimeout) {
        clearTimeout(this.scrollCursorTimeout);
        this.scrollCursorTimeout = null;
      }
    };
  }

  private unbindChatScrollCursor(): void {
    this.chatScrollCursorCleanup?.();
    this.chatScrollCursorCleanup = null;
  }

  /** Scroll after DOM/layout updates (send, receive, mic, typewriter). */
  private scheduleScrollToBottom(): void {
    if (!this.platformBrowser) {
      return;
    }

    queueMicrotask(() => {
      this.scrollRafId = requestAnimationFrame(() => {
        if (this.suppressAutoScroll) {
          this.scrollRafId = null;
          return;
        }
        this.scrollChatToBottom();
        this.scrollRafId = requestAnimationFrame(() => {
          if (this.suppressAutoScroll) {
            this.scrollRafId = null;
            return;
          }
          this.scrollChatToBottom();
          this.scrollRafId = null;
        });
      });
    });
  }

  private cancelScheduledScroll(): void {
    if (this.scrollRafId !== null) {
      cancelAnimationFrame(this.scrollRafId);
      this.scrollRafId = null;
    }
  }

  private scrollChatToBottom(): void {
    try {
      const el = this.chatContainer?.nativeElement;
      if (!el) {
        return;
      }

      el.scrollTop = el.scrollHeight;
    } catch (err) {
      console.warn('scrollChatToBottom failed', err);
    }
  }

  private isChatNearBottom(): boolean {
    const el = this.chatContainer?.nativeElement;
    if (!el) {
      return true;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    return distanceFromBottom <= 80;
  }

  // optional: stop everything immediately
  stopSpeaking(): void {
    if (this.malayalamTts.isMalayalamLanguage(this.langugae())) {
      this.malayalamTts.stop();
      this.malayalamPiperTts.stop();
    }

    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      this.currentUtterance = null;
    }
  }

  /** Stops bot TTS/typewriter so the user can send or use the mic. */
  private interruptAssistantSpeech(): void {
    this.speechSessionId++;

    this.stopSpeaking();
    this.flushPresentationQueueOnInterrupt();

    if (this.typingAnimationInterval) {
      clearInterval(this.typingAnimationInterval);
      this.typingAnimationInterval = null;
    }

    this.clearDisplaySyncInterval();

    if (this.activeSpeechMessage) {
      this.activeSpeechMessage.displayedMessage =
        this.activeSpeechMessage.message;
      this.revealMessageMedia(this.activeSpeechMessage);
      this.activeSpeechMessage = null;
    }

    this.isTypingBotResponse.set(false);
    this.voiceAssistant.notifyPlaybackInterrupted();
    this.cd.detectChanges();
  }

  private startBreathe() {
    if (!this.platformBrowser) return;
    this.stopBreathe();
    const start = Date.now();
    this.breatheInterval = setInterval(() => {
      const elapsed = Date.now() - start;
      const val = (Math.sin(elapsed / 250) + 1) / 2; // 0 to 1
      this.volumeScale.set(1 + val * 0.1); // breathe between 1.0 and 1.1
    }, 16);
  }

  private stopBreathe() {
    if (this.breatheInterval) {
      clearInterval(this.breatheInterval);
      this.breatheInterval = null;
    }
    if (!this.loader()) {
      this.volumeScale.set(1);
    }
  }

  ngOnDestroy() {
    this.bootstrapAborted = true;
    this.leaveActiveChat();
    this.chatResizeObserver?.disconnect();
    this.unbindChatScrollCursor();
    this.cancelScheduledScroll();
    this.subs.unsubscribe();
    this.closeCameraDialogIfOpen();
    this.stopSpeaking();
    this.voiceAssistant.notifyChatClosed();
    this.stopBreathe();
    if (this.platformBrowser && 'speechSynthesis' in window) {
      window.speechSynthesis.removeEventListener(
        'voiceschanged',
        this.onSynthesisVoicesChanged
      );
    }
  }

  private initSynthesisVoices(): void {
    if (!('speechSynthesis' in window)) {
      return;
    }
    this.refreshSynthesisVoices();
    window.speechSynthesis.addEventListener(
      'voiceschanged',
      this.onSynthesisVoicesChanged
    );
  }

  private refreshSynthesisVoices(): void {
    if (!('speechSynthesis' in window)) {
      return;
    }
    this.synthesisVoices = window.speechSynthesis.getVoices();
  }

  private syncSpeechLanguage(): void {
    const lang = localStorage.getItem('language') || 'en';
    this.langugae.set(lang);
  }

  private getSpeechLang(): string {
    return resolveSpeechLang(this.langugae());
  }

  private applySpeechVoice(utterance: SpeechSynthesisUtterance): void {
    this.syncSpeechLanguage();

    const voices =
      this.synthesisVoices.length > 0
        ? this.synthesisVoices
        : window.speechSynthesis.getVoices();
    const voice = applyFemaleSpeechVoiceToUtterance(
      utterance,
      voices,
      this.langugae()
    );

    const voiceLabel = voice
      ? `${voice.name} (${voice.lang}, quality ${speechVoiceQuality(voice)}/4)`
      : `browser default (${utterance.lang})`;
    if (voiceLabel !== this.loggedSpeechVoice) {
      this.loggedSpeechVoice = voiceLabel;
      // Dev mode only (every environment here sets production: true): shows which
      // voice each browser ends up with.
      if (isDevMode()) {
        console.info(`[AI Chat] TTS voice: ${voiceLabel}`);
      }
    }
  }
}
